import { useState } from "react";
import { motion } from "framer-motion";
import {
  ShoppingCart, Home, PhoneCall, Headphones, ClipboardList, Megaphone,
  Users, Clock, Wallet, Check, Minus, Plus, ArrowRight, HelpCircle,
} from "lucide-react";
import Reveal from "./Reveal";
import { useSite } from "@/context/SiteContext";
import { api, formatApiErrorDetail, track } from "@/lib/api";

const SERVICES = [
  { name: "E-commerce Support", icon: ShoppingCart },
  { name: "Real Estate Operations", icon: Home },
  { name: "Appointment Setting", icon: PhoneCall },
  { name: "Cold Calling and Phone Support", icon: Headphones },
  { name: "Administrative and Executive Assistance", icon: ClipboardList },
  { name: "Social Media Management and Content", icon: Megaphone },
];

export default function TeamEstimate() {
  const { openBooking } = useSite();
  const [service, setService] = useState("");
  const [specialists, setSpecialists] = useState(3);
  const [hours, setHours] = useState("30");
  const [budget, setBudget] = useState("3000");
  const [basis, setBasis] = useState("Total monthly budget");
  const [notes, setNotes] = useState("");
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const SelectedIcon = SERVICES.find((s) => s.name === service)?.icon || HelpCircle;

  const submit = async () => {
    setError("");
    if (!service) { setError("Please choose your support type in Step 1."); return; }
    if (!form.name || !form.email) { setError("Please fill in your name and work email."); return; }
    setBusy(true);
    try {
      await api.post("/team-estimate", { service, specialists, hours, budget, basis, notes, ...form });
      track("estimate_submit");
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="section-pad relative overflow-hidden bg-white text-[#0F172A]"
      data-testid="estimate-section"
    >
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10">
        <Reveal direction="left">
          <div className="eyebrow text-[#0096D6] mb-4">Available talent</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-[46px] tracking-tight leading-[1.12] max-w-3xl">
            Build your team. Make your <span className="text-[#00A3E0]">budget go further.</span>
          </h2>
          <p className="mt-4 text-[#47546E] text-base sm:text-lg max-w-2xl leading-relaxed">
            Choose your support, team size, and budget. We will email you a tailored quote.
          </p>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-[1.15fr_1fr] gap-8 items-start">
          <Reveal direction="left" className="space-y-6">
            {/* Step 1 */}
            <div className="rounded-2xl bg-white border border-[#E7EDF7] shadow-[0_4px_10px_rgba(15,23,42,0.05),0_14px_34px_rgba(13,33,84,0.10),0_30px_60px_rgba(29,111,242,0.10)] p-6 sm:p-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-8 rounded-full bg-[#1D6FF2] text-white font-display font-extrabold text-sm flex items-center justify-center">1</span>
                <h3 className="font-display font-extrabold text-lg">Choose your support</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {SERVICES.map((s) => {
                  const sel = service === s.name;
                  return (
                    <button
                      key={s.name}
                      onClick={() => setService(s.name)}
                      className={`relative rounded-xl border p-4 text-left transition-all duration-200 ${sel ? "border-[1.5px] border-[#1D6FF2] bg-[#F0F7FF] shadow-[0_8px_24px_rgba(29,111,242,0.18)]" : "border-[#E7EDF7] bg-white hover:border-[#9CC4F5]"}`}
                      data-testid={`estimate-service-${SERVICES.indexOf(s)}`}
                    >
                      {sel && (
                        <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#1D6FF2] flex items-center justify-center">
                          <Check size={11} className="text-white" strokeWidth={3.5} />
                        </span>
                      )}
                      <s.icon size={22} className={sel ? "text-[#1D6FF2]" : "text-[#5A6B8C]"} strokeWidth={2.2} />
                      <div className="mt-2.5 text-[13px] font-bold leading-snug text-[#0F172A]">{s.name}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2 */}
            <div className="rounded-2xl bg-white border border-[#E7EDF7] shadow-[0_4px_10px_rgba(15,23,42,0.05),0_14px_34px_rgba(13,33,84,0.10),0_30px_60px_rgba(29,111,242,0.10)] p-6 sm:p-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-8 rounded-full bg-[#1D6FF2] text-white font-display font-extrabold text-sm flex items-center justify-center">2</span>
                <h3 className="font-display font-extrabold text-lg">Build your team</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold text-[#5A6B8C] uppercase tracking-wide mb-2">Number of specialists</label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSpecialists((n) => Math.max(1, n - 1))}
                      className="w-11 h-11 rounded-xl border border-[#D8E3F5] flex items-center justify-center text-[#1D6FF2] hover:bg-[#F0F7FF] transition-colors"
                      aria-label="Fewer specialists"
                      data-testid="estimate-stepper-minus"
                    >
                      <Minus size={17} strokeWidth={2.5} />
                    </button>
                    <span className="w-14 text-center font-display font-black text-2xl" data-testid="estimate-specialists-count">{specialists}</span>
                    <button
                      onClick={() => setSpecialists((n) => Math.min(50, n + 1))}
                      className="w-11 h-11 rounded-xl border border-[#D8E3F5] flex items-center justify-center text-[#1D6FF2] hover:bg-[#F0F7FF] transition-colors"
                      aria-label="More specialists"
                      data-testid="estimate-stepper-plus"
                    >
                      <Plus size={17} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-[#5A6B8C] uppercase tracking-wide mb-2">Hours per specialist per week</label>
                  <select className="booking-input" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="estimate-hours">
                    <option value="20">20 hours</option>
                    <option value="30">30 hours</option>
                    <option value="40">40 hours</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="rounded-2xl bg-white border border-[#E7EDF7] shadow-[0_4px_10px_rgba(15,23,42,0.05),0_14px_34px_rgba(13,33,84,0.10),0_30px_60px_rgba(29,111,242,0.10)] p-6 sm:p-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-8 rounded-full bg-[#1D6FF2] text-white font-display font-extrabold text-sm flex items-center justify-center">3</span>
                <h3 className="font-display font-extrabold text-lg">Your budget</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold text-[#5A6B8C] uppercase tracking-wide mb-2">Preferred budget USD</label>
                  <input className="booking-input" type="number" min="0" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="estimate-budget" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-[#5A6B8C] uppercase tracking-wide mb-2">Budget basis</label>
                  <select className="booking-input" value={basis} onChange={(e) => setBasis(e.target.value)} data-testid="estimate-basis">
                    <option value="Total monthly budget">Total monthly budget</option>
                    <option value="Per specialist per month">Per specialist per month</option>
                  </select>
                </div>
                <textarea
                  className="booking-input sm:col-span-2"
                  rows={3}
                  placeholder="Tell us about your goals, tools, or any other details (optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="estimate-notes"
                />
              </div>
            </div>
          </Reveal>

          {/* Summary card */}
          <Reveal direction="right">
            <div className="rounded-2xl bg-white border border-[#E7EDF7] shadow-[0_4px_10px_rgba(15,23,42,0.05),0_18px_44px_rgba(13,33,84,0.12),0_40px_80px_rgba(29,111,242,0.13)] p-7 sm:p-8 lg:sticky lg:top-24" data-testid="estimate-summary-card">
              <h3 className="font-display font-extrabold text-xl text-[#0F172A]">Your team request</h3>

              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-3.5">
                  <span className="w-10 h-10 rounded-xl bg-[#E8F2FF] flex items-center justify-center flex-shrink-0"><SelectedIcon size={18} className="text-[#1D6FF2]" /></span>
                  <div>
                    <div className="text-xs font-bold text-[#5A6B8C] uppercase tracking-wide">Support</div>
                    <div className="text-sm font-bold text-[#0F172A]" data-testid="estimate-summary-service">{service || "Not selected yet"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3.5">
                  <span className="w-10 h-10 rounded-xl bg-[#E8F2FF] flex items-center justify-center flex-shrink-0"><Users size={18} className="text-[#1D6FF2]" /></span>
                  <div>
                    <div className="text-xs font-bold text-[#5A6B8C] uppercase tracking-wide">Team size</div>
                    <div className="text-sm font-bold text-[#0F172A]" data-testid="estimate-summary-specialists">{specialists} specialist{specialists === 1 ? "" : "s"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3.5">
                  <span className="w-10 h-10 rounded-xl bg-[#E8F2FF] flex items-center justify-center flex-shrink-0"><Clock size={18} className="text-[#1D6FF2]" /></span>
                  <div>
                    <div className="text-xs font-bold text-[#5A6B8C] uppercase tracking-wide">Coverage</div>
                    <div className="text-sm font-bold text-[#0F172A]" data-testid="estimate-summary-hours">{hours} hours per specialist per week</div>
                  </div>
                </div>
                <div className="flex items-center gap-3.5">
                  <span className="w-10 h-10 rounded-xl bg-[#E8F2FF] flex items-center justify-center flex-shrink-0"><Wallet size={18} className="text-[#1D6FF2]" /></span>
                  <div>
                    <div className="text-xs font-bold text-[#5A6B8C] uppercase tracking-wide">Budget</div>
                    <div className="text-sm font-bold text-[#0F172A]" data-testid="estimate-summary-budget">${budget || "0"} — {basis.toLowerCase()}</div>
                  </div>
                </div>
              </div>

              <div className="my-6 border-t border-[#E7EDF7]" />

              {done ? (
                <div className="text-center py-6" data-testid="estimate-success">
                  <span className="w-14 h-14 rounded-full bg-[#E8F2FF] flex items-center justify-center mx-auto">
                    <Check size={26} className="text-[#1D6FF2]" strokeWidth={3} />
                  </span>
                  <h4 className="mt-4 font-display font-extrabold text-lg">Request sent!</h4>
                  <p className="mt-2 text-sm text-[#47546E]">A confirmation is on its way to {form.email}. Our team will email your tailored quote shortly.</p>
                  <button className="btn btn-outline-dark w-full mt-6" onClick={() => { setDone(false); setService(""); setSpecialists(3); setNotes(""); setForm({ name: "", email: "", company: "" }); }}>
                    Make another request
                  </button>
                </div>
              ) : (
                <>
                  <div className="text-sm font-bold text-[#0F172A] mb-4">Where should we send your quote?</div>
                  <div className="space-y-3">
                    <input className="booking-input" placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="estimate-input-name" />
                    <input className="booking-input" type="email" placeholder="Work email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="estimate-input-email" />
                    <input className="booking-input" placeholder="Company name" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="estimate-input-company" />
                  </div>
                  {error && <div className="mt-3 text-sm text-red-500" data-testid="estimate-error">{error}</div>}
                  <button className="btn btn-primary w-full mt-5" onClick={submit} disabled={busy} data-testid="estimate-submit-button">
                    {busy ? "Sending..." : "Request My Team Estimate"} <ArrowRight size={16} strokeWidth={2.5} />
                  </button>
                  <p className="mt-3 text-xs text-[#64748B] text-center">Tailored quote by email. Rates confirmed after review.</p>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
